import { apiClient } from '../../services/api';
import { createClientRequestId } from '../../utils/clientRequestId';

const BASE = '/api/business/delivery';
const HEADERS = { 'X-Portal': 'business' };
const unwrap = (response) => response?.data?.data ?? response?.data ?? response;

export const businessDeliveryOperationsService = Object.freeze({
  async pickups(businessId, cursor, signal) {
    return unwrap(await apiClient.get(`${BASE}/pickups`, { headers: HEADERS, params: { businessId, cursor }, signal }));
  },
  async pickup(businessId, tripId, signal) {
    return unwrap(await apiClient.get(`${BASE}/pickups/${tripId}`, { headers: HEADERS, params: { businessId }, signal }));
  },
  async directory(businessId, resource, filters={}, signal) {
    if (!['parcels','rider-directory'].includes(resource)) throw new Error('Unknown directory');
    return unwrap(await apiClient.get(`${BASE}/${resource}`,{headers:HEADERS,params:{businessId,...filters},signal}));
  },
  async prepare(businessId,riderId) {
    return unwrap(await apiClient.post(`${BASE}/riders/${riderId}/prepare`,{businessId},{headers:HEADERS}));
  },
  async capacity(businessId,riderId,capacity,expectedVersion) {
    return unwrap(await apiClient.patch(`${BASE}/riders/${riderId}/capacity`,{businessId,capacity,expectedVersion},{headers:HEADERS}));
  },
  async correctPickup(businessId,runId,input) {
    return unwrap(await apiClient.post(`${BASE}/runs/${runId}/pickup-corrections`,{businessId,...input},{headers:HEADERS}));
  },
  async clearPickupHold(businessId,shipmentId) {
    return unwrap(await apiClient.post(`${BASE}/shipments/${shipmentId}/clear-pickup-hold`,{businessId},{headers:HEADERS}));
  },
  async releasePickup(businessId, runId, input = {}) {
    return unwrap(await apiClient.post(`${BASE}/runs/${runId}/pickup-release`, {
      businessId,
      expectedRunVersion: input.expectedRunVersion,
      manifestVersion: input.manifestVersion,
      scannedLabelCodes: input.scannedLabelCodes,
    }, { headers: {
      ...HEADERS,
      'Idempotency-Key': input.idempotencyKey || createClientRequestId('handoff_issue'),
    } }));
  },
  // Target-v2 only (dark). An Assignment Problem is an audited report, never a
  // decline: resolving it is an explicit, reasoned, version-bound Business
  // decision that either keeps the commitment or requeues dispatch.
  async assignmentProblems(businessId, filters = {}, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/assignment-problems`, {
      headers: HEADERS, params: { businessId, ...filters }, signal: options.signal,
    }));
  },
  async resolveAssignmentProblem(businessId, assignmentId, problemId, input = {}) {
    return unwrap(await apiClient.post(
      `${BASE}/assignments/${assignmentId}/problems/${problemId}/resolve`,
      { businessId, ...input },
      { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('problem_resolve') } },
    ));
  },
  // APP-DEL-019: what Riders reported from the road. Read-only.
  async riderReports(businessId, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/rider-reports`, { headers: HEADERS, params: { businessId }, signal: options.signal }));
  },
  async stopDispositions(businessId, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/stop-dispositions`, {
      headers: HEADERS, params: { businessId }, signal: options.signal,
    }));
  },
  async decideStopDisposition(businessId, attemptId, input = {}) {
    const { idempotencyKey, ...decision } = input;
    return unwrap(await apiClient.post(
      `${BASE}/attempts/${attemptId}/disposition`,
      { businessId, ...decision },
      { headers: {
        ...HEADERS,
        'Idempotency-Key': idempotencyKey || createClientRequestId('stop_disposition'),
      } },
    ));
  },
  // WP7 target-v2 (dark). The receiving desk. Confirming receipt is the only
  // command in WP7 that moves Parcel custody, and it moves it for the scanned
  // Parcels alone - a Parcel nobody took stays with the Rider and stays visible.
  async returnSessions(businessId, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/return-sessions`, {
      headers: HEADERS, params: { businessId }, signal: options.signal,
    }));
  },
  async confirmReturnReceipt(businessId, returnSessionId, input = {}) {
    return unwrap(await apiClient.post(
      `${BASE}/return-sessions/${returnSessionId}/confirm`,
      { businessId, ...input },
      { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('return_confirm') } },
    ));
  },
  // What happens to a Parcel that came back. Re-admission is bounded: a Shipment
  // gets one automatic cycle, then it has to be decided another way.
  async returnedParcels(businessId, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/returned-parcels`, {
      headers: HEADERS, params: { businessId }, signal: options.signal,
    }));
  },
  async decideReturnDisposition(businessId, shipmentId, input = {}) {
    return unwrap(await apiClient.post(
      `${BASE}/shipments/${shipmentId}/return-disposition`,
      { businessId, ...input },
      { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('return_disposition') } },
    ));
  },
  // A manager's final decision on a returned Parcel the desk escalated.
  async decideReturnAfterManagementReview(businessId, shipmentId, input = {}) {
    return unwrap(await apiClient.post(
      `${BASE}/shipments/${shipmentId}/return-management-decision`,
      { businessId, ...input },
      { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('return_management_decision') } },
    ));
  },
  // A cancellation raised after the Parcel left with the Rider. Approving it
  // stops the handover and nothing else.
  async postCustodyCancellations(businessId, filters = {}, options = {}) {
    return unwrap(await apiClient.get(`${BASE}/post-custody-cancellations`, {
      headers: HEADERS, params: { businessId, ...filters }, signal: options.signal,
    }));
  },
  async decidePostCustodyCancellation(businessId, cancellationId, input = {}) {
    return unwrap(await apiClient.post(
      `${BASE}/post-custody-cancellations/${cancellationId}/decision`,
      { businessId, ...input },
      { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('cancellation_decision') } },
    ));
  },
  async board(businessId, filters = {}, options = {}) {
    const response = await apiClient.get(`${BASE}/operations-board`, {
      headers: HEADERS, params: { businessId, ...filters }, signal: options.signal,
    });
    return unwrap(response);
  },
  async dispatch(businessId) {
    const response = await apiClient.post(`${BASE}/dispatch-batches`, {
      businessId, triggerType: 'MANUAL', cutoff: new Date().toISOString(),
    }, { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('dispatch_batch') } });
    return unwrap(response);
  },
  async run(businessId, runId, options = {}) {
    const response = await apiClient.get(`${BASE}/runs/${runId}`, {
      headers: HEADERS, params: { businessId }, signal: options.signal,
    });
    return unwrap(response);
  },
  async proofs(businessId, filters = {}, options = {}) {
    const response = await apiClient.get(`${BASE}/proofs`, {
      headers: HEADERS, params: { businessId, ...filters }, signal: options.signal,
    });
    return unwrap(response);
  },
  async proof(businessId, proofId) {
    const response = await apiClient.get(`${BASE}/proofs/${proofId}`, {
      headers: HEADERS, params: { businessId },
    });
    return unwrap(response);
  },
  async reviewProof(businessId, proofId, decision, note) {
    const response = await apiClient.post(`${BASE}/proofs/${proofId}/review`, {
      businessId, decision, note,
    }, { headers: { ...HEADERS, 'Idempotency-Key': createClientRequestId('proof_review') } });
    return unwrap(response);
  },
});
